import { useState, useCallback } from "react";
import { Flex } from "@radix-ui/themes";
import { ConversationsSidebar } from "./parts/ConversationsSidebar";
import styles from "./GoodResponsesLibrary.module.scss";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";
import { CheckmarkBadgeIcon } from "@/v2/assets/icons/CheckmarkBadgeIcon";
import {
  ReleaseConversationFeedback,
  ReleaseConversation,
} from "@/v2/entities/release-conversation/ReleaseConversation.model";
import { ConversationDetail } from "./parts/ConversationDetail";

export function GoodResponsesLibrary() {
  const [selectedConversationId, setSelectedConversationId] = useState<
    number | null
  >(null);
  const [selectedConversation, setSelectedConversation] =
    useState<ReleaseConversation | null>(null);
  const [searchPhrase, setSearchPhrase] = useState("");
  const [semanticSearchPhrase, setSemanticSearchPhrase] = useState("");
  const [feedbackType, setFeedbackType] =
    useState<ReleaseConversationFeedback>("POSITIVE");
  const [topicIds, setTopicIds] = useState<number[]>([]);
  const [quackUserId, setQuackUserId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const handleConversationSelect = useCallback(
    (conversation: ReleaseConversation) => {
      setSelectedConversationId(conversation.id);
      setSelectedConversation(conversation);
    },
    []
  );

  const handleClearSelection = useCallback(() => {
    setSelectedConversationId(null);
    setSelectedConversation(null);
  }, []);

  const handleShowMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const handleFeedbackTypeChange = useCallback((value: string) => {
    if (
      value === "POSITIVE" ||
      value === "FORGOTTEN" ||
      value === "NEGATIVE" ||
      value === "NONE"
    ) {
      setFeedbackType(value);
      setPage(1);
      setSelectedConversationId(null);
    }
  }, []);

  const handleShowSimilar = useCallback((searchText: string) => {
    setSemanticSearchPhrase(searchText);
    setPage(1);
  }, []);

  return (
    <PageLayoutV2>
      <Flex className={styles.goodResponsesLibraryContainer}>
        <Flex direction="column" className={styles.sidebarPanel}>
          <PageHeader
            title="Good Feedback Library"
            description="Browse and manage positive feedback conversations"
            icon={
              <CheckmarkBadgeIcon
                size={24}
                fill={PageColors[ENavigate.GOOD_RESPONSES_LIBRARY] || "gray"}
              />
            }
          />
          <ConversationsSidebar
            selectedConversationId={selectedConversationId}
            onConversationSelect={handleConversationSelect}
            onClearSelection={handleClearSelection}
            onConversationDataChange={setSelectedConversation}
            searchPhrase={searchPhrase}
            onSearchPhraseChange={setSearchPhrase}
            semanticSearchPhrase={semanticSearchPhrase}
            onSemanticSearchPhraseChange={setSemanticSearchPhrase}
            feedbackType={feedbackType}
            onFeedbackTypeChange={handleFeedbackTypeChange}
            topicIds={topicIds}
            onTopicIdsChange={setTopicIds}
            quackUserId={quackUserId}
            onQuackUserIdChange={setQuackUserId}
            page={page}
            onShowMore={handleShowMore}
            selectedIds={selectedIds}
            onSelectedIdsChange={setSelectedIds}
          />
        </Flex>

        <div className={styles.divider} />

        <Flex className={styles.detailPanel}>
          <ConversationDetail
            conversation={selectedConversation}
            onFeedbackUpdate={() => {
              setSelectedConversation(null);
            }}
            onShowSimilar={handleShowSimilar}
          />
        </Flex>
      </Flex>
    </PageLayoutV2>
  );
}
